import * as React from "react";
import {
  Flex,
  useMultiStyleConfig,
  chakra,
  StylesProvider,
  useStyles,
  Box,
  LinkProps as CkLinkProps,
} from "@chakra-ui/react";
import { Link, LinkProps, To, useLocation, useMatch } from "react-router-dom";
import { contextErrors } from "messages";

const DUPLICATE_SECTION_PARAM_KEYS = [
  "destinationChartCode",
  "copiedFromChartCode",
  "copiedSectionCode",
] as const;

function sanitizeSearchParams(search: string): string {
  if (!search) {
    return "";
  }

  const nextParams = new URLSearchParams(search);

  DUPLICATE_SECTION_PARAM_KEYS.forEach((paramKey) => {
    nextParams.delete(paramKey);
  });

  const nextSearch = nextParams.toString();

  return nextSearch ? `?${nextSearch}` : "";
}

type NavigationButtonProps = Omit<LinkProps, "to"> &
  CkLinkProps & {
    to: string;
    parent?: string;
    disabled?: boolean;
    isLastSaved?: boolean;
    isActive?: boolean;
  };

type NavigationButtonContentProps = {
  label: string;
  labelIcon: React.ReactNode;
  isSaved?: boolean;
};

type NavigationButtonContextValue = {
  match: boolean;
};

const NavigationButtonContext =
  React.createContext<NavigationButtonContextValue>({ match: false });

function useNavigationButtonContext(child: string) {
  const context = React.useContext(NavigationButtonContext);

  if (!context) {
    throw new Error(contextErrors.missingParent(child, "NavigationButton"));
  }

  return context;
}

const NavigationButtonContent: React.FC<NavigationButtonContentProps> = (
  props
) => {
  const { label, labelIcon, isSaved } = props;
  const styles = useStyles();
  const { match } = useNavigationButtonContext("NavigationButtonContent");

  const iconColor =
    isSaved !== undefined && !isSaved ? { color: "gray.450" } : {};

  return (
    <Flex __css={styles.content}>
      {labelIcon && (
        <chakra.span display="flex">
          {React.isValidElement(labelIcon) &&
            React.cloneElement(
              labelIcon as React.ReactElement<Record<string, unknown>>,
              {
                __css: styles.labelicon,
                "aria-selected": match,
                ...iconColor,
              }
            )}
        </chakra.span>
      )}
      <chakra.p
        title={label}
        __css={styles.label}
        aria-selected={match}
        color={isSaved !== undefined && !isSaved ? "gray.700" : ""}
      >
        {label}
      </chakra.p>
    </Flex>
  );
};

type NavigationButtonIndicatorProps = {
  children?: React.ReactNode;
};

function NavigationButtonIndicator({
  children,
}: NavigationButtonIndicatorProps) {
  const styles = useStyles();

  return <chakra.span __css={styles.indicator}>{children}</chakra.span>;
}

function NavigationButton(props: NavigationButtonProps) {
  const {
    children,
    colorScheme,
    disabled,
    isLastSaved,
    isActive: isActiveProp,
    ...rest
  } = props;
  const location = useLocation();

  const { to: restTo, parent: restParent, ...restProps } = rest;
  const to: To = React.useMemo(() => {
    if (typeof restTo === "string") {
      if (restTo.includes("?")) return restTo;
      return `${restTo}${sanitizeSearchParams(location.search)}`;
    }
    return "/";
  }, [restTo, location.search]);

  const cleanTo = to.split(/[?#]/)[0];
  const cleanParent = restParent?.split(/[?#]/)[0] || "";

  const toMatch = useMatch({ path: cleanTo, end: false });
  const parentMatch = useMatch({ path: cleanParent, end: false });

  const computedActive = restParent ? !!parentMatch : !!toMatch;
  const isActive =
    typeof isActiveProp === "boolean" ? isActiveProp : computedActive;

  const styles = useMultiStyleConfig("NavigationButton", {
    ...restProps,
    colorScheme,
  });

  const contextValue = React.useMemo(() => ({ match: isActive }), [isActive]);

  return (
    <>
      {disabled ? (
        <chakra.a
          __css={styles.root}
          aria-selected={!!isActive}
          {...restProps}
          display="flex"
          justifyContent="start"
          alignItems="center"
        >
          <Box __css={styles.container} pl={isLastSaved ? "6px" : "11px"}>
            <NavigationButtonContext.Provider value={contextValue}>
              <StylesProvider value={styles}>{children}</StylesProvider>
            </NavigationButtonContext.Provider>
          </Box>
        </chakra.a>
      ) : (
        <chakra.a
          as={Link}
          to={to}
          __css={styles.root}
          role="link"
          {...restProps}
          display="flex"
          justifyContent="start"
          alignItems="center"
        >
          {isLastSaved && (
            <Box bgColor="orange" w="5px" h="36px" borderRadius="2px" />
          )}
          <Box
            __css={styles.container}
            aria-selected={!!isActive}
            pl={isLastSaved ? "6px" : "11px"}
          >
            <NavigationButtonContext.Provider value={contextValue}>
              <StylesProvider value={styles}>{children}</StylesProvider>
            </NavigationButtonContext.Provider>
          </Box>
        </chakra.a>
      )}
    </>
  );
}

export type { NavigationButtonProps, NavigationButtonContentProps };
export { NavigationButton, NavigationButtonContent, NavigationButtonIndicator };
